import React, { useState, useEffect } from 'react';
import {
  BookOpen, FileText, BookMarked, StickyNote,
  Download, Bookmark, BookmarkCheck, Eye, Calendar, User, Tag, Filter, X,
  ExternalLink, ChevronLeft, ChevronRight, Upload, ArrowLeft, ZoomIn, ZoomOut,
  Maximize, Minimize, RotateCw, MoreVertical, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/components/ui/use-toast';
import { toast as sonnerToast } from 'sonner';
import { cn } from '@/lib/utils';
import { Document, Page, pdfjs } from 'react-pdf';
import mammoth from 'mammoth';
import { db, storage } from '@/firebase/config';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '@/components/pdf-sandbox-mobile.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// --- Shared Types & Config ---

export interface Resource {
  id: string;
  title: string;
  subject: string;
  grade: string;
  examType: string;
  type: string;
  description: string;
  tags: string[];
  author?: string;
  isBookmarked: boolean;
  downloadCount: number;
  fileType: string;
  fileSize: string;
  uploadDate: string;
  pdfUrl: string;
  year?: string;
}

export interface FilterOptions {
  subject: string[];
  grade: string[];
  examType: string[];
  type: string[];
}

export const resourceCategoriesConfig = [
  { id: 'books', name: 'Books', icon: 'BookOpen', type: 'book' },
  { id: 'papers', name: 'Past Papers', icon: 'FileText', type: 'paper' },
  { id: 'guides', name: 'Revision Guides', icon: 'BookMarked', type: 'guide' },
  { id: 'notes', name: 'Notes', icon: 'StickyNote', type: 'notes' },
  { id: 'bookmarks', name: 'Bookmarks', icon: 'Star', type: 'bookmarks' },
];

export const subjects = ['Math', 'Physics', 'Chemistry', 'Biology', 'English', 'History', 'Geography', 'Computer Science'];
export const grades = ['Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];
export const examTypes = ['GCSE', 'IGCSE', 'A-Level', 'IB', 'Cambridge', 'KCSE'];
export const resourceTypes = [
  { value: 'book', label: 'Book' },
  { value: 'paper', label: 'Paper' },
  { value: 'guide', label: 'Study Guide' },
  { value: 'notes', label: 'Notes' },
];

export const typeIcons = {
  book: BookOpen,
  paper: FileText,
  guide: BookMarked,
  notes: StickyNote,
};

export const iconMap: { [key: string]: any } = {
  BookOpen,
  FileText,
  BookMarked,
  StickyNote,
};

// --- Components ---

// 1. ResourceCard
interface ResourceCardProps {
  resource: Resource;
  onBookmark: (id: string) => void;
  onDownload: (resource: Resource) => void;
  onView: (resource: Resource) => void;
  viewMode?: 'grid' | 'list';
  isDownloading?: boolean;
}

export function ResourceCard({ resource, onBookmark, onDownload, onView, viewMode = 'grid', isDownloading = false }: ResourceCardProps) {
  const TypeIcon = typeIcons[resource.type as keyof typeof typeIcons] || FileText;

  const handleBookmark = () => {
    onBookmark(resource.id);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'book':
        return 'bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20';
      case 'paper':
        return 'bg-[#6B7280]/10 text-[#6B7280] border-[#6B7280]/20';
      case 'guide':
        return 'bg-[#22C55E]/10 text-[#22C55E] border-[#22C55E]/20';
      case 'notes':
        return 'bg-[#6B7280]/10 text-[#6B7280] border-[#6B7280]/20';
      default:
        return 'bg-[#6B7280]/10 text-[#6B7280] border-[#6B7280]/20';
    }
  };

  if (viewMode === 'list') {
    return (
      <Card className="resource-card group border shadow-sm hover:shadow-md flex flex-row p-4 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] rounded-xl">
        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className={cn('p-2 rounded-md border', getTypeColor(resource.type))}>
                <TypeIcon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-base mb-1 group-hover:text-[#2563EB] transition-colors">
                  {resource.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                  {resource.description}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBookmark}
              className="p-2 h-10 w-10 hover:bg-gray-50 dark:hover:bg-[#131E31]"
              aria-label={resource.isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
              {resource.isBookmarked ? (
                <BookmarkCheck className="h-5 w-5 text-[#22C55E]" />
              ) : (
                <Bookmark className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              )}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <Badge variant="outline" className="text-xs font-medium bg-[#22C55E]/10 dark:bg-[#22C55E]/20 text-[#15803d] dark:text-[#4ADE80] border-[#22C55E]/20 hover:bg-[#22C55E]/20">
              {resource.subject}
            </Badge>
            <span className="text-gray-500 dark:text-gray-400">Grade: {resource.grade}</span>
            <span className="text-gray-500 dark:text-gray-400">{resource.examType}</span>
            <span className="text-gray-500 dark:text-gray-400">{resource.year}</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView(resource)}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
              aria-label={`View ${resource.title}`}
            >
              <Eye className="h-4 w-4 mr-2" />
              {resource.type === 'book' ? 'Read' : resource.type === 'paper' ? 'View Paper' : 'View'}
            </Button>
            <Button
              size="sm"
              onClick={() => onDownload(resource)}
              className="bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]"
              aria-label={`Download ${resource.title}`}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {isDownloading ? 'Downloading...' : 'Download'}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="resource-card group border shadow-sm hover:shadow-md min-h-[380px] flex flex-col bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] rounded-xl">
      <CardContent className="p-3 flex-1">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className={cn('p-1.5 rounded-md border', getTypeColor(resource.type))}>
              <TypeIcon className="h-4 w-4" />
            </div>
            <Badge variant="outline" className="text-xs capitalize border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
              {resource.type}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBookmark}
            className="p-1 h-8 w-8 hover:bg-gray-50 dark:hover:bg-[#131E31]"
            aria-label={resource.isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            {resource.isBookmarked ? (
              <BookmarkCheck className="h-4 w-4 text-[#22C55E]" />
            ) : (
              <Bookmark className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            )}
          </Button>
        </div>
        <h3 className="font-semibold text-sm mb-2 line-clamp-2 group-hover:text-[#2563EB] transition-colors">
          {resource.title}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
          {resource.description}
        </p>
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-gray-400">Subject:</span>
            <Badge variant="outline" className="text-xs font-medium bg-[#22C55E]/10 dark:bg-[#22C55E]/20 text-[#15803d] dark:text-[#4ADE80] border-[#22C55E]/20 hover:bg-[#22C55E]/20 max-w-[120px] truncate">
              {resource.subject}
            </Badge>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-gray-400">Grade:</span>
            <span className="font-medium truncate">{resource.grade}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 dark:text-gray-400">Exam:</span>
            <span className="font-medium truncate max-w-[120px]">{resource.examType}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
          {/* Download count and file size hidden as requested */}
          <div className="flex items-center space-x-1 col-span-2">
            <Calendar className="h-3 w-3" />
            <span className="truncate">{resource.year}</span>
          </div>
        </div>
        {resource.author && (
          <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400 mb-3">
            <User className="h-3 w-3" />
            <span className="truncate">{resource.author}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-1">
          {resource.tags && Array.isArray(resource.tags) && resource.tags.slice(0, 2).map((tag: string) => (
            <Badge
              key={tag}
              variant="outline"
              className="text-xs px-1.5 py-0 border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
            >
              <Tag className="h-2 w-2 mr-1" />
              <span className="truncate max-w-[60px]">{tag}</span>
            </Badge>
          ))}
          {resource.tags.length > 2 && (
            <Badge variant="outline" className="text-xs px-1.5 py-0 border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
              +{resource.tags.length - 2}
            </Badge>
          )}
        </div>
      </CardContent>
      <div className="p-3 pt-0 flex flex-col gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onView(resource)}
          className="w-full bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
          aria-label={`View ${resource.title}`}
        >
          <Eye className="h-4 w-4 mr-2" />
          <span className="truncate">
            {resource.type === 'book' ? 'Read' : resource.type === 'paper' ? 'View Paper' : 'View'}
          </span>
        </Button>
        <Button
          size="sm"
          onClick={() => onDownload(resource)}
          className="w-full bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]"
          aria-label={`Download ${resource.title}`}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {isDownloading ? 'Downloading...' : 'Download'}
        </Button>
      </div>
    </Card>
  );
}

// 2. ResourceFilters
interface ResourceFiltersProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  activeFiltersCount: number;
}

export function ResourceFilters({ filters, onFilterChange, activeFiltersCount }: ResourceFiltersProps) {
  const updateFilter = (key: keyof FilterOptions, value: string) => {
    const currentValues = filters[key];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    onFilterChange({
      ...filters,
      [key]: newValues,
    });
  };

  const clearFilters = () => {
    onFilterChange({
      subject: [],
      grade: [],
      examType: [],
      type: [],
    });
  };

  const removeFilter = (key: keyof FilterOptions, value: string) => {
    onFilterChange({
      ...filters,
      [key]: filters[key].filter((v) => v !== value),
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between sm:justify-start sm:space-x-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
              aria-label="Open filters"
            >
              <Filter className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Filters</span>
              {activeFiltersCount > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-2 h-5 w-5 p-0 text-xs bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white dark:text-green-900"
                >
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 sm:w-96 p-4 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" align="start" side="bottom">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Filters</h4>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-gray-700 dark:text-gray-300 hover:text-[#2563EB]"
                    aria-label="Clear all filters"
                  >
                    Clear all
                  </Button>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Subject</label>
                <Select onValueChange={(value) => updateFilter('subject', value)}>
                  <SelectTrigger className="w-full bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    {subjects.map((subject) => (
                      <SelectItem
                        key={subject}
                        value={subject}
                        className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1E293B] cursor-pointer"
                      >
                        {subject}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Grade</label>
                <Select onValueChange={(value) => updateFilter('grade', value)}>
                  <SelectTrigger className="w-full bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    {grades.map((grade) => (
                      <SelectItem
                        key={grade}
                        value={grade}
                        className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1E293B] cursor-pointer"
                      >
                        {grade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Exam Type</label>
                <Select onValueChange={(value) => updateFilter('examType', value)}>
                  <SelectTrigger className="w-full bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
                    <SelectValue placeholder="Select exam type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    {examTypes.map((type) => (
                      <SelectItem
                        key={type}
                        value={type}
                        className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1E293B] cursor-pointer"
                      >
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Resource Type</label>
                <Select onValueChange={(value) => updateFilter('type', value)}>
                  <SelectTrigger className="w-full bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    {resourceTypes.map((t) => (
                      <SelectItem
                        key={t.value}
                        value={t.value}
                        className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1E293B] cursor-pointer"
                      >
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {activeFiltersCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
            aria-label="Clear all filters"
          >
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        )}
      </div>
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {filters.subject.map((subject) => (
            <Badge key={`subject-${subject}`} variant="secondary" className="pr-1 bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white">
              Subject: {subject}
              <Button variant="ghost" size="sm" className="h-4 w-4 p-0 ml-1 hover:bg-transparent" onClick={() => removeFilter('subject', subject)}>
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
          {/* Repeat for other details omitted for brevity but they are same logic */}
        </div>
      )}
    </div>
  );
}

// 3. ResourcePreview
interface ResourcePreviewProps {
  resource: Resource | null;
  isOpen: boolean;
  onClose: () => void;
  onDownload: (resource: Resource) => void;
  onBookmark: (id: string) => void;
  onView: (resource: Resource) => void;
}

export function ResourcePreview({ resource, isOpen, onClose, onDownload, onBookmark, onView }: ResourcePreviewProps) {
  if (!resource) return null;
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[80vh] p-0 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl">
        <DialogHeader className="p-6 pb-4 border-b border-[#F1F5F9] dark:border-[#1E293B]">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <DialogTitle className="text-xl font-semibold pr-8">{resource.title}</DialogTitle>
              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="capitalize border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300">{resource.type}</Badge>
                <Badge variant="secondary" className="bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white">{resource.subject}</Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 mt-4">
             <Button variant="outline" size="sm" onClick={() => onBookmark(resource.id)}>
               {resource.isBookmarked ? <BookmarkCheck className="h-4 w-4 mr-2" /> : <Bookmark className="h-4 w-4 mr-2" />}
               {resource.isBookmarked ? 'Bookmarked' : 'Bookmark'}
             </Button>
             <Button size="sm" onClick={() => onDownload(resource)} className="bg-[#2563EB]">
               <Download className="h-4 w-4 mr-2" /> Download
             </Button>
             <Button variant="outline" size="sm" onClick={() => onView(resource)}>
               <Eye className="h-4 w-4 mr-2" /> View
             </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 p-6">
          <div className="h-full bg-gray-100/30 flex items-center justify-center">
             <div className="text-center">
                <ExternalLink className="h-8 w-8 text-[#2563EB] mx-auto mb-2" />
                <h3 className="font-medium text-lg">Resource Preview</h3>
                <p className="text-gray-500">{resource.description}</p>
             </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// 4. BookUploadForm
// 4. BookUploadForm
export function BookUploadForm({ onClose, onSubmit }: { onClose?: () => void, onSubmit?: (data: Resource) => Promise<void> }) {
  const [formData, setFormData] = useState({
    title: '', subject: '', grade: '', examType: '', type: '', description: '', author: '', year: new Date().getFullYear().toString(), tags: [] as string[]
  });
  const [customGrade, setCustomGrade] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [customExamType, setCustomExamType] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const { toast } = useToast();

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      }
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      // Use custom grade/subject/examType if provided
      const gradeToSave = customGrade.trim() ? customGrade.trim() : formData.grade;
      const subjectToSave = customSubject.trim() ? customSubject.trim() : formData.subject;
      const examTypeToSave = customExamType.trim() ? customExamType.trim() : formData.examType;
      const storageRef = ref(storage, `global_resources/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const resourceData: Resource = {
         id: '',
         ...formData,
         grade: gradeToSave,
         subject: subjectToSave,
         examType: examTypeToSave,
         description: formData.description || '',
         isBookmarked: false,
         downloadCount: 0,
         fileType: file.type.split('/')[1]?.toUpperCase() || 'PDF',
         fileSize: (file.size / 1024 / 1024).toFixed(2) + ' MB',
         uploadDate: new Date().toISOString(),
         pdfUrl: url
      }
      const docRef = await addDoc(collection(db, 'global_resources'), resourceData);
      resourceData.id = docRef.id;
      onSubmit?.(resourceData);
      onClose?.();
      toast({ title: 'Success', description: 'Resource uploaded successfully' });
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: 'Failed to upload resource', variant: 'destructive' });
      setUploading(false);
    }
  };

  return (
    <ScrollArea className="h-[70vh] pr-4">
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                     <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Title</Label>
                     <Input
                       value={formData.title}
                       onChange={e => setFormData({...formData, title: e.target.value})}
                       required
                       placeholder="Resource Title"
                       className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                     />
                </div>
                <div className="space-y-2">
                     <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Author</Label>
                     <Input
                       value={formData.author}
                       onChange={e => setFormData({...formData, author: e.target.value})}
                       placeholder="Author Name"
                       className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                     />
                </div>
                <div className="space-y-2">
                     <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Subject</Label>
                     <div className="flex gap-2">
                       <Select value={formData.subject} onValueChange={v => setFormData({...formData, subject: v})}>
                          <SelectTrigger className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100">
                            <SelectValue placeholder="Select Subject" />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                            {subjects.map(s => (
                              <SelectItem key={s} value={s} className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:bg-slate-100 dark:focus:bg-slate-800/60 cursor-pointer">
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                       </Select>
                       <Input
                         value={customSubject}
                         onChange={e => setCustomSubject(e.target.value)}
                         placeholder="Or type custom subject"
                         className="w-40 bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                       />
                     </div>
                </div>
                 <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Grade <span className="text-[10px] text-slate-400 dark:text-slate-500 normal-case font-medium">(optional)</span>
                    </Label>
                    <div className="flex gap-2">
                      <Select value={formData.grade} onValueChange={v => setFormData({...formData, grade: v})}>
                        <SelectTrigger className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100">
                          <SelectValue placeholder="Select Grade (optional)" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                          {grades.map(s => (
                            <SelectItem key={s} value={s} className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:bg-slate-100 dark:focus:bg-slate-800/60 cursor-pointer">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={customGrade}
                        onChange={e => setCustomGrade(e.target.value)}
                        placeholder="Or type custom grade"
                        className="w-40 bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                      />
                    </div>
                 </div>
                <div className="space-y-2">
                     <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Exam Type</Label>
                     <div className="flex gap-2">
                       <Select value={formData.examType} onValueChange={v => setFormData({...formData, examType: v})}>
                          <SelectTrigger className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100">
                            <SelectValue placeholder="Select Exam Type" />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                            {examTypes.map(s => (
                              <SelectItem key={s} value={s} className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:bg-slate-100 dark:focus:bg-slate-800/60 cursor-pointer">
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                       </Select>
                       <Input
                         value={customExamType}
                         onChange={e => setCustomExamType(e.target.value)}
                         placeholder="Or type custom exam type"
                         className="w-40 bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                       />
                     </div>
                </div>
                <div className="space-y-2">
                     <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Resource Type</Label>
                     <Select value={formData.type} onValueChange={v => setFormData({...formData, type: v})}>
                        <SelectTrigger className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                          {resourceTypes.map(s => (
                            <SelectItem key={s.value} value={s.value} className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:bg-slate-100 dark:focus:bg-slate-800/60 cursor-pointer">
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                     </Select>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Year</Label>
                    <Input
                      type="number"
                      value={formData.year}
                      onChange={e => setFormData({...formData, year: e.target.value})}
                      className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100"
                    />
                </div>
            </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Description <span className="text-[10px] text-slate-400 dark:text-slate-500 normal-case font-medium">(optional)</span>
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the resource..."
                  className="min-h-[100px] bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                />
              </div>
            <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Tags</Label>
                <div className="flex gap-2 mb-2 flex-wrap">
                    {formData.tags.map(tag => (
                        <Badge
                          key={tag}
                          variant="secondary"
                          className="cursor-pointer bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/30"
                          onClick={() => removeTag(tag)}
                        >
                            {tag} <X className="h-3 w-3 ml-1" />
                        </Badge>
                    ))}
                </div>
                <Input
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                    placeholder="Type tag and press Enter"
                    className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                />
            </div>
            <div className="space-y-2">
                 <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">File</Label>
                 <Input
                   type="file"
                   onChange={e => setFile(e.target.files?.[0] || null)}
                   required
                   className="cursor-pointer bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-700 dark:text-slate-300 file:bg-indigo-50 file:dark:bg-indigo-500/20 file:text-indigo-700 file:dark:text-indigo-300 file:border-0 file:rounded-md file:px-3 file:py-1 file:mr-3 file:font-bold file:text-xs file:cursor-pointer"
                 />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800/60">
                <Button
                  variant="outline"
                  onClick={onClose}
                  type="button"
                  className="rounded-lg border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={uploading}
                  className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                    {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...</> : 'Upload Resource'}
                </Button>
            </div>
        </form>
    </ScrollArea>
  );
}

// 5. PDFReader
interface PDFReaderProps {
  pdfUrl: string;
  title: string;
  onBack: () => void;
  setIsFullscreen: (value: boolean) => void;
}

export function PDFReader({ pdfUrl, title, onBack, setIsFullscreen }: PDFReaderProps) {
  const [zoom, setZoom] = useState(() => {
    // Set initial zoom based on screen size
    return window.matchMedia('(max-width: 768px)').matches ? 100 : 70;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isFullscreen, setLocalIsFullscreen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [docContent, setDocContent] = useState<string>('');
  const [isWordDoc, setIsWordDoc] = useState(false);
  const [isPptx, setIsPptx] = useState(false);
  const [initialWidth, setInitialWidth] = useState<number>(Math.min(window.innerWidth * 0.8, 800));
  const [loading, setLoading] = useState(true);
  const [pdfRendered, setPdfRendered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Legacy viewer toggle: some PDFs render blank with react-pdf (JPEG2000, unusual fonts).
  // The legacy mode uses the browser\'s native PDF renderer via an <iframe>, which handles
  // those edge-cases without modifying or removing the current modern viewer.
  const [useLegacyViewer, setUseLegacyViewer] = useState(false);
  const { toast } = useToast();

  const toggleFullscreen = () => {
    setLocalIsFullscreen(prev => {
      const n = !prev;
      setIsFullscreen(n);
      return n;
    });
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
    setLoading(false);
    setPdfRendered(true);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load the PDF. Please ensure the file is valid and try again.');
    setLoading(false);
    toast({
      title: 'Error',
      description: 'Failed to load the PDF. Please try again.',
      style: {
        background: '#FEF2F2',
        color: '#7F1D1D',
        border: '1px solid #FECACA',
        padding: '10px',
        borderRadius: '8px',
      },
      duration: 5000,
    });
  };

  useEffect(() => {
    const calculateWidth = () => {
      try {
        const screenWidth = window.innerWidth || 800;
        const maxWidth = 1000;
        const padding = screenWidth < 768 ? 32 : 64;
        setInitialWidth(Math.min(screenWidth - padding, maxWidth));
      } catch (e) {
        setInitialWidth(800);
      }
    };
    calculateWidth();
    window.addEventListener('resize', calculateWidth);
    return () => window.removeEventListener('resize', calculateWidth);
  }, []);

  useEffect(() => {
     const ext = pdfUrl.split('.').pop()?.toLowerCase() || '';
     if (['doc', 'docx', 'htm', 'html'].includes(ext)) {
         setIsWordDoc(true);
         setLoading(false);
         fetch(pdfUrl).then(r => r.arrayBuffer()).then(b => mammoth.convertToHtml({arrayBuffer: b}))
         .then(r => setDocContent(r.value))
         .catch(e => {
            console.error(e);
            setError('Failed to load document');
            setLoading(false);
         });
     } else if (['ppt', 'pptx'].includes(ext)) {
         setIsPptx(true);
         setDocContent(pdfUrl);
         setLoading(false);
     } else {
         setDocContent(pdfUrl);
         setLoading(false);
     }
  }, [pdfUrl]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 10, 50));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleDownload = async () => {
    try {
      // Resolve the final URL the same way the old code did (handles both
      // storage paths and direct http links).
      let downloadUrl = pdfUrl;
      if (!pdfUrl.startsWith('http')) {
         const storageRef = ref(storage, pdfUrl);
         downloadUrl = await getDownloadURL(storageRef);
      }

      // Fetch the file as a blob so the user never sees the Firebase storage URL
      // in their browser downloads bar. Same pattern as LessonPlannerView's
      // handleDownloadResource.
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const cleanUrl = downloadUrl.split('?')[0].split('#')[0];
      const fileExtension = cleanUrl.split('.').pop()?.toLowerCase() || 'pdf';

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${title}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

      sonnerToast.info(`Downloading ${title}...`);
    } catch (error) {
      console.error('Error downloading file:', error);
      sonnerToast.error('Download Failed', { description: 'Unable to download the file. Please try again later.' });
    }
  };

  return (
    <div className={`flex flex-col h-full bg-gray-50 dark:bg-gray-900 ${isFullscreen ? 'fixed inset-0 z-50' : 'relative'}`}>
      <div className="border-b bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
        <div className="flex items-center justify-between p-2 sm:p-4 gap-2 sm:gap-4 max-w-full sticky top-0 z-10">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <Button
              variant="outline"
              onClick={onBack}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Back to resources"
            >
              <ArrowLeft className="h-4 w-4 mr-0 md:mr-2 text-gray-700 dark:text-gray-300" />
              <span className="hidden md:inline text-gray-700 dark:text-gray-300">Back</span>
            </Button>
            <h1 className="text-lg font-semibold truncate text-gray-900 dark:text-white">{title}</h1>
          </div>

          <div className="sm:hidden ml-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open PDF actions">
                  <MoreVertical className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <div className="flex flex-col gap-2">
                  {!useLegacyViewer && (
                    <>
                      <Button variant="ghost" size="sm" onClick={handlePrevPage} disabled={currentPage === 1} className="justify-start">
                        <ChevronLeft className="h-4 w-4 mr-2" /> Prev
                      </Button>
                      <Button variant="ghost" size="sm" onClick={handleNextPage} disabled={currentPage === totalPages} className="justify-start">
                        Next <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </>
                  )}
                  <div className="flex flex-col gap-1 px-1">
                    <Button variant="ghost" size="sm" onClick={handleZoomOut} className="justify-start">
                      <ZoomOut className="h-4 w-4 mr-2" />
                      Zoom Out
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleZoomIn} className="justify-start">
                      <ZoomIn className="h-4 w-4 mr-2" />
                      Zoom In
                    </Button>
                  </div>
                  {!useLegacyViewer && (
                    <Button variant="ghost" size="sm" onClick={handleRotate} className="justify-start">
                      <RotateCw className="h-4 w-4 mr-2" /> Rotate
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={toggleFullscreen} className="justify-start">
                    {isFullscreen ? <Minimize className="h-4 w-4 mr-2" /> : <Maximize className="h-4 w-4 mr-2" />}
                    {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleDownload} className="justify-start">
                    <Download className="h-4 w-4 mr-2" /> Download
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setUseLegacyViewer(p => !p)} className="justify-start">
                    {useLegacyViewer ? '← Modern Viewer' : 'Legacy PDF.js'}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {!isWordDoc && totalPages > 0 && !useLegacyViewer && (
            <div className="hidden sm:flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap px-2">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="hidden sm:flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomOut}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-700 dark:text-gray-300 min-w-[3rem] text-center">{zoom}%</span>
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomIn}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            {!isWordDoc && !useLegacyViewer && (
              <Button
                variant="outline"
                size="icon"
                onClick={handleRotate}
                className="hidden sm:inline-flex bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
                aria-label="Rotate document"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={toggleFullscreen}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <Minimize className="h-4 w-4 mr-2" /> : <Maximize className="h-4 w-4 mr-2" />}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleDownload}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300"
              aria-label="Download document"
            >
              <Download className="h-4 w-4" />
            </Button>
            {/* Legacy PDF.js toggle — shown only for non-word-docs */}
            {!isWordDoc && (
              <Button
                variant={useLegacyViewer ? 'default' : 'outline'}
                size="sm"
                onClick={() => setUseLegacyViewer(p => !p)}
                title="Some PDFs appear blank in the modern viewer. Switch to the browser-native legacy viewer for better compatibility."
                className={`hidden sm:inline-flex text-xs px-2 ${
                  useLegacyViewer
                    ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500'
                    : 'bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300'
                }`}
                aria-label="Toggle legacy PDF viewer"
              >
                {useLegacyViewer ? '← Modern' : 'Legacy PDF.js'}
              </Button>
            )}
          </div>
        </div>
        <div className="px-4 pb-3 pt-1">
          <Slider
            value={[zoom]}
            onValueChange={(value) => setZoom(value[0])}
            min={50}
            max={200}
            step={10}
            className="w-full max-w-xs mx-auto"
            aria-label="Zoom level"
          />
        </div>
      </div>
      <div className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900">
        <div className="h-full w-full overflow-x-hidden overflow-y-auto"> 
          <div className="flex items-center justify-center min-h-full p-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-10 w-10 animate-spin text-[#2563EB]" />
              </div>
            ) : error ? (
              <div className="text-center">
                <p className="text-destructive">{error}</p>
                <Button
                  variant="outline"
                  onClick={onBack}
                  className="mt-4 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB]"
                  aria-label="Back to resources"
                >
                  Back to Resources
                </Button>
              </div>
            ) : isWordDoc ? (
              <Card
                className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] shadow-md max-w-4xl w-full mx-auto"
                style={{
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: 'top center',
                  transition: 'transform 0.2s ease-in-out',
                }}
              >
                <div className="p-8 prose prose-sm max-w-none text-gray-900 dark:text-white" dangerouslySetInnerHTML={{ __html: docContent }} />
              </Card>
            ) : isPptx ? (
              <div className="w-full h-full min-h-[600px]" style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center', transition: 'transform 0.2s ease-in-out' }}>
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docContent)}`}
                  className="w-full h-full min-h-[600px] border-0 rounded-lg shadow-md"
                  title={title}
                  sandbox="allow-scripts allow-same-origin allow-popups"
                />
              </div>
            ) : useLegacyViewer ? (
              // Legacy viewer: delegates to the browser's built-in PDF renderer.
              // This handles JPEG2000, unusual font maps, and other formats unsupported by PDF.js.
              <div className="w-full flex justify-center py-4" style={{ height: '85vh' }}>
                <div 
                  className="transition-all duration-200 ease-in-out"
                  style={{ 
                    width: Math.max(300, Math.round(initialWidth * (zoom / 100))),
                    height: '100%' 
                  }}
                >
                  <iframe
                    src={`${pdfUrl}${pdfUrl.includes('#') ? '&' : '#'}toolbar=0&navpanes=0&view=FitH`}
                    title={title}
                    className="w-full h-full border-none rounded-lg shadow-md bg-white"
                    allow="fullscreen"
                  />
                </div>
              </div>
            ) : (
              <div className="pdf-viewport overflow-x-hidden overflow-y-auto w-full h-full">
                  <div className="pdf-container">
                  <div
                    className={`${pdfRendered ? 'pdf-surface rounded-lg shadow-md bg-white dark:bg-[#020617]' : 'flex justify-center items-center'}`}
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: 'top center',
                    transition: 'transform 0.2s ease-in-out',
                  }}
                >
                  <Document
                    file={docContent}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                      <div className="flex items-center justify-center p-6 min-h-[500px]">
                        <Loader2 className="h-10 w-10 animate-spin text-[#2563EB]" />
                      </div>
                    }
                    error={<div className="text-destructive">Error loading PDF. Please try again.</div>}
                  >
                    <Page
                      pageNumber={currentPage}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      width={Math.max(300, Math.round(initialWidth * (zoom / 100)))}
                      className="max-w-full h-auto"
                    />
                  </Document>
                </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
